import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { StatsGrid, type StatTile } from "@/components/ui/stats-grid";
import { ApiError } from "@/lib/api-client";
import { useAuth } from "@/contexts/auth-context";
import { useCurrentUser } from "@/hooks/queries/useCurrentUser";
import { useOrgAdminDashboard } from "@/hooks/queries/useOrgAdminDashboard";
import { useOrgAdminPulse } from "@/hooks/queries/useOrgAdminPulse";
import type {
  DashboardClinicianData,
  SeatPoolData,
} from "@/hooks/queries/org-admin-dashboard-types";
import { formatDate } from "@/lib/format-date";
import { contractNotice } from "@/routes/org-admin/contract-notice";
import { formatPulseTimestamp } from "@/routes/org-admin/pulse-timestamp";
import { renewalBlockNotice } from "@/routes/org-admin/renewal-block-notice";
import {
  accountStatePill,
  calibrationPill,
  countCalibrationComplete,
  countExpiringSoon,
  ROSTER_SCOPE_SENTENCE,
  SUBSCRIPTION_EXPIRY_WINDOW_DAYS,
  seatHealthPercent,
  termStartLine,
} from "@/routes/org-admin/dashboard-derivations";
import {
  SeatRequestBanner,
  useSeatRequest,
} from "@/routes/org-admin/seat-request-cta";

// Story 14.1 — org_admin dashboard home (Screen 25 + UX §13.5, FR76).
// Seat-pool KPIs labelled Capacity / Consumed / Remaining (amended FR83 —
// the mockup's "Assigned/Available" predates the relabel), a derived
// expiring-subscriptions banner, the Seat health card, and the clinician
// roster table (UX §13.5 places it on the dashboard; the mockup's Screen 26
// roster informed the columns). Deliberate omissions per the story's W3
// contract: Invite clinician (14.2) CTA, Actions column (14.4/14.5).
// Derivation helpers live in dashboard-derivations.ts.
//
// Story 14.13 (gh-657) closed the last of those omissions: the Pulse timeline
// now has a data source (the audit store, read through /org-admin/pulse). It is
// a SEPARATE query on purpose — see useOrgAdminPulse — so the panel carries its
// own pending / empty / failure states and neither half of this screen can take
// the other down. The panel is BUILT but is NOT rendered in the M5 delivery;
// the switch and the reason for it are at PULSE_PANEL_ENABLED below.
//
// Story 14.7 (gh-171, FR77 + FR78) — the "Request additional seats" CTA in
// the header: a ONE-STEP action (the Screen-25 modal is stale vs the FR77
// update — documented W3 waiver; the backend accepts no body). It lives in
// the header, OUTSIDE the data/error branching, so it is reachable in every
// screen state. Story 14.11 moved its mechanics to seat-request-cta.ts so
// Screen 26 mounts the same control (session-disable, focus handling, and
// error posture are shared, not duplicated).
//
// Story 14.6 (D4) reached this screen late: the roster is the rows behind the
// Consumed tile, so it lists every consumed seat including removed ones, and it
// carries the State column that makes a dead slot legible. The tile and the
// table are read together or not at all — a count that no visible row explains
// is a CITS ticket per cancellation, which is the failure mode D4 exists to
// prevent.
//
// Story 14.12 (gh-680) — the organization's contract window is now VISIBLE:
// a warn banner names the state and the remedy, and the write affordance is
// disabled with a stated reason instead of leading to a 403 ORG-010. The banner
// coexists with the expiring-subscriptions one — they are independent signals
// (a live contract can still have lapsing individual subscriptions).
//
// Tokens only; no pure-white background, no hardcoded hex; numeric cells use
// tabular-nums (CLAUDE.md constraints).

/** The roster's visible heading, which is also the table's accessible name. */
const ROSTER_HEADING_ID = "org-admin-dashboard-roster-heading";

/** The Pulse panel's heading, which is also the feed's accessible name. */
const PULSE_HEADING_ID = "org-admin-dashboard-pulse-heading";

/**
 * Whether the Story 14.13 "Pulse · last 7 days" panel is rendered.
 *
 * The panel is COMPLETE and tested: `PulseSection` and `useOrgAdminPulse` are
 * below, `GET /org-admin/pulse` answers, and the behaviour is covered by the
 * component tests in `__tests__/OrgAdminDashboardScreen.test.tsx`. It is
 * withheld from the M5 delivery (8 September) by a PRODUCT decision about scope
 * and ships in M6. It is not a defect, not a rollback, and not a workaround for
 * something broken — nothing here is known to be wrong.
 *
 * The implementation stays on the branch rather than being removed because
 * removal was costed and rejected: `administration/admin_console/query.py`
 * imports `application/audit/contract.py`, which arrived in the Pulse commit;
 * fifteen files from that commit were edited again afterwards; and
 * `m5pulseorgidx001` is the sole alembic head.
 *
 * Turning the panel on for M6 is flipping this constant to `true`. There is
 * nothing else to wire and nothing else to un-wire, which is why it is a
 * constant rather than a backend setting: an environment variable would pull in
 * the W6 env-parity gate and one SSM parameter per environment for a switch
 * that flips exactly once. Registered as `seam-1413-pulse-panel-mount` in the W4
 * seam register so a finished-but-invisible feature cannot be quietly forgotten.
 */
const PULSE_PANEL_ENABLED = false;

export function OrgAdminDashboardScreen({
  // The one production call site (`routes/app-routes.tsx`) passes nothing, so
  // the delivered screen always reads the constant above. The prop exists so the
  // component tests can still drive the switched-ON world and keep asserting the
  // panel in its REAL placement — a suite that could only see the off state
  // would go on passing if `PulseSection` were deleted outright.
  pulseEnabled = PULSE_PANEL_ENABLED,
}: {
  pulseEnabled?: boolean;
} = {}) {
  const { user } = useAuth();
  const { data: me } = useCurrentUser();
  const { data, error, isPending, isError, refetch } = useOrgAdminDashboard();

  const seatRequest = useSeatRequest();

  // 409 ORG-002 — the D1 fallback shape: an org_admin whose claim resolves to
  // no professional org (account exists, org linkage pending with CITS). A
  // deterministic state, not a transient failure — calm notice, no retry.
  const hasNoProfessionalOrg =
    error instanceof ApiError && error.code === "ORG-002";

  // JWT claims carry no names (PHI-free) — the greeting reads /auth/me and
  // falls back to the session email (the useCurrentUser consumer convention).
  const displayName =
    [me?.first_name, me?.last_name].filter(Boolean).join(" ") ||
    user?.email ||
    "";

  // Story 14.6 (D4) + PDM 2026-08-13: a surface that shows a seat COUNTER shows
  // the rows behind it. The Consumed tile and the Seat-health bar are on this
  // screen, so the roster below them is every seat the org has consumed —
  // removed rows included, in their own state. The two live-coverage numbers
  // (calibration-complete KPI, expiring-subscriptions banner) scope removed and
  // CITS-blocked rows out themselves; see dashboard-derivations.
  //
  // No client-side sort: the query already returns removed rows last
  // (`ORDER BY (seat.removed_at IS NOT NULL), …`, 14.6 boundary #9), so both
  // screens inherit one ordering rather than re-deriving it twice.
  const clinicians = data?.clinicians ?? [];
  const expiringCount = data ? countExpiringSoon(clinicians, new Date()) : 0;

  // Story 21.3 (AC12) — the next term is sized below the people in it. Null
  // while pending and null when nothing is blocked: the same branch, because a
  // screen must not warn about a commercial state it has not read.
  const renewalBlock = renewalBlockNotice(data?.renewal_block);

  // Story 14.12 — the organization's commercial window. Null while the query is
  // pending or errored, so no affordance is gated on a state we have not read.
  const contract = contractNotice(data?.contract_status, data?.contract_end_date);

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="display-xs text-[color:var(--text-primary)]">
            Welcome back, {displayName}.
          </h1>
          <p className="text-sm text-[color:var(--text-tertiary)]">
            Maintain seat coverage, shepherd calibration readiness, and watch
            adoption without diving into clinical record detail.
          </p>
        </div>
        {/* Header toolbar. "Add person" (Story 14.2, Screen 34) is a secondary
            affordance routing to the invitation form; "Request additional seats"
            stays the single primary CTA (one click = one POST, no modal — FR77).
            Story 14.12: on a non-active contract "Add person" is disabled and
            SAYS why — the backend would refuse it 403 ORG-010, and a link that
            walks the admin into a refusal is the thing this story exists to
            remove. "Request additional seats" is NEVER gated: it is the named
            exemption, and the only way out of the state. */}
        <div className="flex shrink-0 flex-col items-start gap-1 md:items-end">
          <div className="flex items-center gap-3">
            {contract ? (
              <Button
                variant="secondary"
                disabled
                aria-describedby="add-person-blocked-reason"
              >
                Add person
              </Button>
            ) : (
              <Button asChild variant="secondary">
                <Link to="/org-admin/add-person">Add person</Link>
              </Button>
            )}
            <Button
              variant="primary"
              onClick={seatRequest.request}
              disabled={seatRequest.disabled}
            >
              Request additional seats
            </Button>
          </div>
          {contract ? (
            <p
              id="add-person-blocked-reason"
              className="text-xs text-[color:var(--text-tertiary)]"
            >
              {contract.title}
            </p>
          ) : null}
        </div>
      </header>

      {contract ? (
        <Banner variant="warn" title={contract.title}>
          {contract.body}
        </Banner>
      ) : null}

      <SeatRequestBanner control={seatRequest} />

      {hasNoProfessionalOrg ? (
        <Banner variant="info" title="No organization yet">
          Your account isn&apos;t linked to an organization seat pool yet. CITS
          sets up organizations directly — once yours is provisioned, the seat
          pool and clinician roster appear here.
        </Banner>
      ) : isError ? (
        <Banner variant="danger" title="Couldn't load your dashboard">
          <div className="flex flex-col gap-2">
            <span>We couldn&apos;t load the seat pool and roster. Please try again.</span>
            <span>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </span>
          </div>
        </Banner>
      ) : null}

      {isPending ? (
        <div
          className="flex items-center justify-center rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-sm text-[color:var(--text-tertiary)]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          Loading your dashboard…
        </div>
      ) : data ? (
        <>
          <StatsGrid
            stats={dashboardStats(data.seat_pool, countCalibrationComplete(clinicians))}
          />

          {expiringCount > 0 ? (
            <Banner
              variant="warn"
              title={`${expiringCount} clinician${expiringCount === 1 ? " has" : "s have"} subscriptions ending in the next ${SUBSCRIPTION_EXPIRY_WINDOW_DAYS} days`}
            >
              Renewal is handled directly by CITS.
            </Banner>
          ) : null}

          <SeatHealthCard
            seatPool={data.seat_pool}
            currentTermStartedAt={data.current_term_started_at}
          />

          {/* Story 21.3 (AC12). Directly BELOW the Seat health card, which is
              where 21.2's term-start line lives — the ruled order, not whichever
              unit landed first. Outside the card rather than inside it: the card
              describes the CURRENT term's seat pool, and this is about the next
              one. Renders nothing at all when `renewal_block` is null or absent. */}
          {renewalBlock && (
            <Banner variant="warn" title={renewalBlock.title}>
              {renewalBlock.body}
            </Banner>
          )}

          {/* Titled "Seats", not "Clinicians": the moment a dead slot is in the
              table the second word is wrong, and the first is what the Consumed
              tile above actually counts. The scope sentence is the shared const,
              so this screen and Screen 26 explain a removed row identically. */}
          <div className="flex flex-col gap-4 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
            <div className="flex flex-col gap-1">
              <h2
                id={ROSTER_HEADING_ID}
                className="text-sm font-semibold text-[color:var(--text-primary)]"
              >
                Seats in your organization
              </h2>
              <p className="text-sm text-[color:var(--text-tertiary)]">
                {ROSTER_SCOPE_SENTENCE}
              </p>
            </div>

            {clinicians.length === 0 ? (
              <Banner variant="info" title="No clinicians on seats yet">
                Clinicians appear here once they are assigned to seats in your
                organization.
              </Banner>
            ) : (
              <RosterTable
                rows={clinicians}
                requiredCaseCount={data.required_case_count}
              />
            )}
          </div>
        </>
      ) : null}

      {/* Story 14.13 — the Pulse sits OUTSIDE the dashboard's branch, below the
          roster, which keeps the mockup's column order (Seat health → Pulse)
          while letting the two queries fail independently. The one state that
          suppresses it on its own merits is the no-organization notice: there is
          no organization to have a pulse, and a second panel repeating that
          would be noise. Both still hold; the panel is simply switched off for
          the M5 delivery, which is the whole of what PULSE_PANEL_ENABLED says. */}
      {pulseEnabled && !hasNoProfessionalOrg ? <PulseSection /> : null}
    </section>
  );
}

/**
 * This screen's four tiles. The grid itself is the shared
 * `components/ui/stats-grid` primitive (Story 15.7 D5); what stays here is the
 * ARRAY, because the labels and their scope are this screen's.
 *
 * The scope qualifier is not decoration. The first three tiles count SEATS
 * (removed ones included — FR83) and the roster below now lists exactly those
 * rows; this fourth one counts live COVERAGE and excludes removed seats. An
 * org with one removed-but-calibrated clinician therefore reads "3" here over
 * four "Complete" pills in the table, and without the qualifier that is the
 * counter/rows mismatch F6 just removed, reappearing one level down.
 */
function dashboardStats(seatPool: SeatPoolData, calibrationComplete: number): StatTile[] {
  return [
    { label: "Capacity", value: seatPool.capacity },
    { label: "Consumed", value: seatPool.consumed },
    { label: "Remaining", value: seatPool.remaining },
    { label: "Calibration complete (active seats)", value: calibrationComplete },
  ];
}

/**
 * "Pulse · last 7 days" (Screen 25, gh-657) — the organization's own recent
 * activity, one server-composed sentence per line, newest first.
 *
 * Finished, and not rendered in the M5 delivery: its only mount is gated by
 * PULSE_PANEL_ENABLED above. Everything below describes the behaviour M6 turns
 * back on, unchanged.
 *
 * Two things this deliberately does not do.
 *
 * It never composes a sentence. Every line arrives rendered from
 * `/org-admin/pulse`; the client decides only the relative date beside it. Two
 * places deciding what an audit event MEANS is how the mockup's illustrative
 * numbers ("Six clinicians…", "Two invitations accepted") end up shipped as
 * fact, and there is no acceptance event behind that second one at all.
 *
 * It never hardcodes the window. The heading reads the `window_days` the query
 * actually filtered on, so the title and the rows underneath it cannot describe
 * different windows — which also means the heading has no window to show until
 * the payload lands, and says plain "Pulse" until then.
 */
function PulseSection() {
  const { data, isPending, isError, refetch } = useOrgAdminPulse();

  // One clock for the whole render, so a list crossing local midnight mid-map
  // cannot label two entries against two different "todays".
  const now = new Date();

  return (
    // `<section>`, not `<div>`. `aria-labelledby` on a generic element is
    // discarded — a `<div>` has no role to attach a name to — so the same
    // attribute that gives the roster table its accessible name would have
    // been a silent no-op here. A named `<section>` maps to `role="region"`,
    // which is what puts "Pulse · last 7 days" in a screen reader's landmark
    // list and lets someone jump to the feed instead of arrowing to it.
    <section
      className="flex flex-col gap-4 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4"
      aria-labelledby={PULSE_HEADING_ID}
    >
      <h2
        id={PULSE_HEADING_ID}
        className="text-sm font-semibold text-[color:var(--text-primary)]"
      >
        {data ? `Pulse \u00b7 last ${data.window_days} days` : "Pulse"}
      </h2>

      {isPending ? (
        <p
          className="text-sm text-[color:var(--text-tertiary)]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          Loading recent activity…
        </p>
      ) : isError ? (
        // Distinct from the empty state below, and distinct on purpose: "we
        // could not read the feed" and "the feed is empty" are opposite facts
        // about the organization, and only one of them is worth acting on.
        <Banner variant="danger" title="Couldn't load recent activity">
          <div className="flex flex-col gap-2">
            <span>
              We couldn&apos;t load your organization&apos;s recent activity.
              The rest of this page is unaffected.
            </span>
            <span>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </span>
          </div>
        </Banner>
      ) : data.entries.length === 0 ? (
        <Banner
          variant="info"
          title={`Nothing has happened in the last ${data.window_days} days`}
        >
          Seat changes, invitations, contract updates, and calibration
          completions appear here as they happen.
        </Banner>
      ) : (
        <ul className="flex flex-col gap-3">
          {data.entries.map((entry) => {
            const when = formatPulseTimestamp(entry.occurred_at, now);
            return (
              <li key={entry.entry_id} className="flex flex-col gap-0.5">
                {when ? (
                  <strong className="text-sm font-semibold text-[color:var(--text-primary)] [font-variant-numeric:tabular-nums]">
                    {when}
                  </strong>
                ) : null}
                <span className="text-sm text-[color:var(--text-tertiary)]">
                  {entry.sentence}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function SeatHealthCard({
  seatPool,
  currentTermStartedAt,
}: {
  seatPool: SeatPoolData;
  currentTermStartedAt: string | null;
}) {
  const pct = seatHealthPercent(seatPool);
  // AC9. Screen 25 renders the term start INSIDE this card, under the
  // percentage sentence — the figures above it are the ones the term scopes.
  // Story 21.3's renewal-blocked banner sits BELOW this card (stacking agreed
  // at the 2026-09-14 chain-check).
  const termStart = termStartLine(currentTermStartedAt);
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
      <h2 className="text-sm font-semibold text-[color:var(--text-primary)]">
        Seat health
      </h2>
      <div
        className="h-2 overflow-hidden rounded-[var(--radius-full)] bg-[color:var(--bg-tertiary)]"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Seat capacity consumed"
      >
        <div
          className="h-full rounded-[var(--radius-full)] bg-[color:var(--bg-brand-solid)]"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-sm text-[color:var(--text-secondary)] [font-variant-numeric:tabular-nums]">
        <strong>{pct}%</strong> of seat capacity consumed ·{" "}
        <strong>{seatPool.remaining}</strong> seats remaining for new
        invitations.
      </p>
      {termStart && (
        <p className="text-xs text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
          {termStart}
        </p>
      )}
    </div>
  );
}

function RosterTable({
  rows,
  requiredCaseCount,
}: {
  rows: DashboardClinicianData[];
  requiredCaseCount: number;
}) {
  // Screen 25's .table is rounded (16px, amps-hifi-screens.css) — unlike the
  // spec-driven LegacyCertQueueScreen this screen HAS a mockup, so rounded-2xl
  // is the W3-faithful shape here (PR #517 review).
  return (
    <div className="overflow-x-auto rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]">
      {/* Named by the VISIBLE heading rather than by an sr-only <caption> — the
          sibling screens caption their tables because they have no visible
          title, and repeating this one's title verbatim would announce it twice
          to the only users who would hear the caption at all. */}
      <table
        className="w-full border-collapse text-left text-sm"
        aria-labelledby={ROSTER_HEADING_ID}
      >
        <thead>
          <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
            <th scope="col" className="px-4 py-3 font-semibold">
              Clinician
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Org role
            </th>
            {/* The State column is what makes a removed row legible rather than
                just present — without it a dead slot is indistinguishable from a
                live one. It restores the `table cols-6` WIDTH UX §13.5 gives this
                roster (it had been sitting at five); the column SET is this
                epic's, not §13.5's — §13.5 lists Clinician / Email /
                Subscription start / Subscription end / Calibration / Actions,
                and 14.1 folded Email into the Clinician cell and dropped Actions
                (the dashboard roster is read-only; the actions live on Screen
                26). Org role and State are 14.11's and 14.6's additions. */}
            <th scope="col" className="px-4 py-3 font-semibold">
              State
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Calibration
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Subscription start
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Subscription end
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            // Inert by design (UX D3): the pill is a flag, not a link — an
            // org_admin has no escalation path to the numeric values.
            const pill = calibrationPill(
              row.calibration_state,
              row.completed_cases,
              requiredCaseCount,
            );
            // Screen 26's row treatment, reused verbatim: the same pill for the
            // same wire state, so one removed clinician does not look like two
            // different situations on two screens.
            // `term_scope` (Story 21.2, AC8) re-labels exactly one cell of that
            // shared treatment — a removed seat a closed term left behind —
            // and it is passed on BOTH screens for the same reason the pill is
            // shared at all.
            const state = accountStatePill(row.account_state, row.term_scope);
            const name =
              [row.first_name, row.last_name].filter(Boolean).join(" ") || "—";
            return (
              <tr
                key={row.user_id}
                className="border-b border-[color:var(--bg-quaternary)] align-middle last:border-b-0"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-[color:var(--text-primary)]">
                    {name}
                  </div>
                  <div className="text-xs text-[color:var(--text-tertiary)]">
                    {row.email}
                  </div>
                </td>
                <td className="px-4 py-3 text-[color:var(--text-secondary)]">
                  {row.organization_role_label}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={state.variant}>{state.label}</Badge>
                </td>
                {/* tabular-nums: the pending pill's "{N}/{M} cases" makes this
                    a numeric column, so its digits must align down the roster
                    like the two date columns do. */}
                <td className="px-4 py-3 [font-variant-numeric:tabular-nums]">
                  <Badge variant={pill.variant}>{pill.label}</Badge>
                </td>
                <td className="px-4 py-3 text-[color:var(--text-secondary)] [font-variant-numeric:tabular-nums]">
                  {formatDate(row.subscription_start_date)}
                </td>
                <td className="px-4 py-3 text-[color:var(--text-secondary)] [font-variant-numeric:tabular-nums]">
                  {formatDate(row.subscription_end_date)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
